import { useEffect, useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { FolderOpen, FileDown, FileText, Mail } from "lucide-react";

const STATEMENTS = [
  ["MF_SOA", "Mutual Fund Statement of Account", "Holdings + monthly transactions (SEBI SOA)"],
  ["PMS_QUARTERLY_REPORT", "PMS Quarterly Report", "Reg-31: composition, value, expenses, risks"],
  ["AIF_SOA", "AIF Statement of Account", "Commitment, calls, NAV, distributions, multiples"],
];

export default function Documents() {
  const [data, setData] = useState(null);
  useEffect(() => { clientApi.get("/documents/list").then((r) => setData(r.data)); }, []);

  const downloadStatement = async (kind, label) => {
    try {
      const resp = await clientApi.get(`/documents/statements/${kind}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(resp.data);
      a.download = `${kind}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${label} downloaded`);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  const downloadDoc = async (d) => {
    try {
      const { data: r } = await clientApi.get(`/documents/${d.id}/download`);
      const a = document.createElement("a");
      a.href = `data:application/octet-stream;base64,${r.content}`;
      a.download = r.file_name;
      a.click();
      toast.success(`${r.file_name} downloaded`);
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><FolderOpen className="h-7 w-7" /> Documents &amp; Statements</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Your statements on demand, documents shared by SMIFS, and everything we've sent you.</p>
      </div>
      {!data ? <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Loading...</div> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            {STATEMENTS.map(([kind, label, desc]) => (
              <Card key={kind} className={`border-[var(--glass-border)] ${!data.available_statements[kind] ? "opacity-50" : ""}`}>
                <CardContent className="flex h-full flex-col p-4">
                  <FileText className="mb-2 h-5 w-5 text-[var(--cta)]" />
                  <div className="text-sm font-medium text-[var(--brand)]">{label}</div>
                  <div className="mb-3 mt-1 flex-1 text-xs text-[var(--ink-muted)]">{desc}</div>
                  <Button size="sm" className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={!data.available_statements[kind]} onClick={() => downloadStatement(kind, label)} data-testid={`stmt-download-${kind}`}>
                    <FileDown className="mr-1 h-3.5 w-3.5" /> Download PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-[var(--glass-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Shared by SMIFS ({data.documents.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {data.documents.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="docs-empty">No documents shared yet — fact sheets, annual reports and notices will appear here.</div> : (
                <div className="divide-y divide-[var(--glass-border)]" data-testid="docs-list">
                  {data.documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <div>
                        <div className="font-medium text-[var(--brand)]">{d.file_name}</div>
                        <div className="text-xs text-[var(--ink-muted)]">{d.doc_type} · {d.product}{d.period ? ` · ${d.period}` : ""} · {formatDate(d.uploaded_at)}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`}><FileDown className="mr-1 h-3 w-3" /> Download</Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-[var(--glass-border)]">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Communications sent to you ({data.communications.length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {data.communications.length === 0 ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="comms-empty">No communications yet.</div> : (
                <div className="divide-y divide-[var(--glass-border)]" data-testid="comms-list">
                  {data.communications.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <Mail className="h-4 w-4 shrink-0 text-[var(--cta)]" />
                      <div className="flex-1">
                        <div className="font-medium text-[var(--brand)]">{c.subject}</div>
                        <div className="text-xs text-[var(--ink-muted)]">{formatDate(c.sent_at)} · {c.channel.replace("_", " ").toLowerCase()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </ClientLayout>
  );
}
